import { ReactElement, useEffect, useMemo, useState } from "react";
import Modal from "../../components/Atomos/Modals/Modal";
import Input from "../../components/Atomos/Inputs/Input";
import InputText from "../../components/staff/InputText";
import DropDownMultiple, {
  type DropDownOptionBase,
} from "../../components/staff/DropDownMultiple";
import Button from "../../components/staff/Button";
import Close from "../../components/Atomos/Icons/Close";
import SaveChanges from "../../components/Atomos/Icons/EditMode/SaveChanges";
import IIntegratorSettings, {
  INTEGRATOR_DEFAULT_IS_SEARCHABLE_VALUES,
  INTEGRATOR_FREQUENCY_PERIODICITIES,
  INTEGRATOR_SETTINGS_ID,
  type IntegratorDefaultIsSearchable,
  type IntegratorFrequencyPeriodicity,
} from "../../interfaces/models/IIntegratorSettings";
import {
  getIntegratorSettings,
  updateIntegratorSettings,
} from "../../Services/IntegratorSettings";
import { toastError, toastSuccess } from "../../utils/toast";

const FOOTER_BUTTON_CLASS = "w-full justify-center sm:!w-auto";

const PERIODICITY_OPTIONS: DropDownOptionBase[] = [
  { id: "days", name: "Days" },
  { id: "weeks", name: "Weeks" },
  { id: "months", name: "Months" },
];

const DEFAULT_IS_SEARCHABLE_OPTIONS: DropDownOptionBase[] = [
  { id: "true", name: "True" },
  { id: "false", name: "False" },
  { id: "notSet", name: "[Not Set]" },
];

const PERIODICITY_SINGULAR: Record<IntegratorFrequencyPeriodicity, string> = {
  days: "day",
  weeks: "week",
  months: "month",
};

type FieldErrors = {
  startDate?: string;
  interval?: string;
};

type GeneralSettingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

function isFrequencyPeriodicity(
  value: string
): value is IntegratorFrequencyPeriodicity {
  return (INTEGRATOR_FREQUENCY_PERIODICITIES as readonly string[]).includes(
    value
  );
}

function isDefaultIsSearchable(
  value: string
): value is IntegratorDefaultIsSearchable {
  return (INTEGRATOR_DEFAULT_IS_SEARCHABLE_VALUES as readonly string[]).includes(
    value
  );
}

function formatStartDate(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) {
    return isoDate;
  }
  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  ).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function intervalLabel(periodicity: IntegratorFrequencyPeriodicity): string {
  const names: Record<IntegratorFrequencyPeriodicity, string> = {
    days: "days",
    weeks: "weeks",
    months: "months",
  };
  return `Interval (${names[periodicity]})`;
}

function readInterval(raw: string): number | null {
  if (!raw.trim()) {
    return null;
  }
  const interval = Number(raw);
  if (!Number.isInteger(interval) || interval < 1) {
    return null;
  }
  return interval;
}

function validateFields(
  startDate: string,
  intervalRaw: string
): FieldErrors {
  const errors: FieldErrors = {};
  if (!startDate.trim()) {
    errors.startDate = "Start date is required.";
  }
  if (readInterval(intervalRaw) == null) {
    errors.interval = "Interval must be a whole number of at least 1.";
  }
  return errors;
}

export default function GeneralSettingsModal({
  isOpen,
  onClose,
}: GeneralSettingsModalProps): ReactElement {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [periodicity, setPeriodicity] =
    useState<IntegratorFrequencyPeriodicity>("weeks");
  const [intervalRaw, setIntervalRaw] = useState("1");
  const [defaultIsSearchable, setDefaultIsSearchable] =
    useState<IntegratorDefaultIsSearchable>("notSet");
  const [errors, setErrors] = useState<FieldErrors>({});

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    setErrors({});

    void (async () => {
      try {
        const settings = await getIntegratorSettings();
        if (cancelled) {
          return;
        }
        setStartDate(settings.frequency?.startDate ?? "");
        setPeriodicity(settings.frequency?.periodicity ?? "weeks");
        setIntervalRaw(String(settings.frequency?.interval ?? 1));
        setDefaultIsSearchable(
          isDefaultIsSearchable(settings.defaultIsSearchable)
            ? settings.defaultIsSearchable
            : "notSet"
        );
      } catch (e: any) {
        if (!cancelled) {
          setLoadError(true);
          toastError(e?.message ?? "Could not load Integrator settings.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const periodicityOption = useMemo(
    () =>
      PERIODICITY_OPTIONS.find((option) => option.id === periodicity) ??
      PERIODICITY_OPTIONS[1],
    [periodicity]
  );

  const defaultIsSearchableOption = useMemo(
    () =>
      DEFAULT_IS_SEARCHABLE_OPTIONS.find(
        (option) => option.id === defaultIsSearchable
      ) ??
      DEFAULT_IS_SEARCHABLE_OPTIONS.find((option) => option.id === "notSet") ??
      DEFAULT_IS_SEARCHABLE_OPTIONS[2],
    [defaultIsSearchable]
  );

  const scheduleSummary = useMemo(() => {
    const interval = readInterval(intervalRaw);
    if (!startDate || interval == null) {
      return null;
    }
    const unit =
      interval === 1
        ? PERIODICITY_SINGULAR[periodicity]
        : periodicityOption.name.toLowerCase();
    return `Every ${interval} ${unit}, starting ${formatStartDate(startDate)}.`;
  }, [startDate, intervalRaw, periodicity, periodicityOption.name]);

  const handleClose = () => {
    if (saving) {
      return;
    }
    onClose();
  };

  const handleSave = async () => {
    if (saving || loading) {
      return;
    }
    const nextErrors = validateFields(startDate, intervalRaw);
    setErrors(nextErrors);
    if (nextErrors.startDate || nextErrors.interval) {
      return;
    }

    const interval = readInterval(intervalRaw);
    if (interval == null) {
      return;
    }

    setSaving(true);
    try {
      const payload: IIntegratorSettings = {
        id: INTEGRATOR_SETTINGS_ID,
        frequency: {
          startDate,
          periodicity,
          interval,
        },
        defaultIsSearchable,
      };
      await updateIntegratorSettings(payload);
      toastSuccess("Settings saved.");
      onClose();
    } catch (e: any) {
      toastError(e?.message ?? "Could not save Integrator settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isModalOpen={isOpen}
      toggleModal={handleClose}
      title="General Settings"
      titleColor="text-secondary font-semibold text-lg"
      compact
      compactBodyScrolls={false}
      className="w-full max-w-lg border border-gray-200 shadow-lg"
      bodyClassName="!pb-8"
    >
      {loading ? (
        <p className="text-sm text-gray-600">Loading settings…</p>
      ) : loadError ? (
        <div className="flex flex-col gap-4">
          <p className="text-sm leading-relaxed text-[#474F56] sm:text-base">
            Could not load Integrator Engine settings.
          </p>
          <div className="mt-2 flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-end">
            <Button
              className={FOOTER_BUTTON_CLASS}
              text="Close"
              onClick={handleClose}
              color="neutral"
              variant="outlined"
              size="fixed"
              icon={<Close />}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <p className="text-sm leading-relaxed text-[#474F56] sm:text-base">
            How often the Integrator Engine should run automatically for every
            enabled firm.
          </p>

          <Input
            title="Start date"
            name="startDate"
            type="date"
            value={startDate}
            required
            error={Boolean(errors.startDate)}
            errorMsj={errors.startDate}
            onChange={(event) => {
              setStartDate(event.target.value);
              if (errors.startDate) {
                setErrors((prev) => ({ ...prev, startDate: undefined }));
              }
            }}
          />

          <div className="flex min-w-0 items-start gap-4">
            <div className="min-w-0 flex-1">
              <DropDownMultiple<DropDownOptionBase>
                title="Periodicity"
                options={PERIODICITY_OPTIONS}
                value={periodicityOption}
                onChange={(option) => {
                  const next = option ? String(option.id) : "weeks";
                  setPeriodicity(isFrequencyPeriodicity(next) ? next : "weeks");
                }}
                searchable={false}
                sortByName={false}
                required
                renderInPortal
              />
            </div>
            <div className="min-w-0 flex-1">
              <InputText
                title={intervalLabel(periodicity)}
                name="interval"
                type="number"
                value={intervalRaw}
                required
                min={1}
                error={Boolean(errors.interval)}
                errorMessage={errors.interval}
                onChange={(value) => {
                  setIntervalRaw(value);
                  if (errors.interval) {
                    setErrors((prev) => ({ ...prev, interval: undefined }));
                  }
                }}
              />
            </div>
          </div>

          {scheduleSummary && (
            <p className="text-sm text-gray-600">{scheduleSummary}</p>
          )}

          <DropDownMultiple<DropDownOptionBase>
            title="Default Person Searchability"
            options={DEFAULT_IS_SEARCHABLE_OPTIONS}
            value={defaultIsSearchableOption}
            onChange={(option) => {
              const next = option ? String(option.id) : "notSet";
              setDefaultIsSearchable(
                isDefaultIsSearchable(next) ? next : "notSet"
              );
            }}
            searchable={false}
            sortByName={false}
            required
            renderInPortal
            helperText="Applied when the Integrator creates a professional. NotSet leaves the field off the document."
          />

          <div className="mt-4 flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-end">
            <Button
              className={FOOTER_BUTTON_CLASS}
              text="Cancel"
              onClick={handleClose}
              color="neutral"
              variant="outlined"
              size="fixed"
              icon={<Close />}
              disabled={saving}
            />
            <Button
              className={FOOTER_BUTTON_CLASS}
              text={saving ? "Saving…" : "Save"}
              onClick={() => void handleSave()}
              color="secondary"
              variant="filled"
              size="fixed"
              icon={<SaveChanges />}
              disabled={saving}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
